import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import { SonicTermData } from "../../common/classes/SonicTerm";
import { SonicTaxonomyData } from "../../common/classes/SonicTaxonomy";
import { getTerms } from "../../services/api/taxonomies/getTerms";
import { postUpdateTerm } from "../../services/api/taxonomies/postUpdateTerm";
import { getTaxonomies } from "../../services/api/taxonomies/getTaxonomies";
import { postUpdateFeaturedTerms } from "../../services/api/taxonomies/postUpdateFeaturedTerms";
import { getFeaturedTerms } from "../../services/api/taxonomies/getFeaturedTerms";

interface TaxState {
  total_entries: number;
  page: number;
  total_pages: number;
  terms: { [key: string]: Array<SonicTermData> };
  taxonomies: Array<SonicTaxonomyData>; //taxonomies: {total_entries:number;page:number;total_pages:number;entries:Array<SonicTaxonomyData>}
  pickups: { [key: string]: Array<SonicTermData> };
}

const initialState: TaxState = {
  total_entries: 0,
  page: 1,
  total_pages: 0,
  terms: {},
  taxonomies: [],
  pickups: {},
};

export const requestTaxonomies = createAsyncThunk(
  "taxonomy/requestTaxonomies",
  async () => {
    try {
      const response = await getTaxonomies();

      if (response === null) return null;

      const tax: Array<SonicTaxonomyData> = [];
      response.forEach((t) => tax.push({ ...t }));

      return tax;
    } catch (error) {
      return null;
    }
  }
);

export const requestTerms = createAsyncThunk(
  "taxonomy/requestTerms",
  async (args?: {
    types?: string | Array<string>;
    page?: number;
    per?: number;
    search?: string;
    desc?: boolean;
  }) => {
    try {
      const result: {
        total_entries: number;
        page: number;
        total_pages: number;
        terms: { [key: string]: Array<SonicTermData> };
      } = {
        total_entries: 0,
        page: 0,
        total_pages: 0,
        terms: {},
      };

      if (args?.types && typeof args.types == "string") {
        const response = await getTerms(
          args.types,
          args.page,
          args.per,
          args.search,
          args.desc
        );

        if (response) {
          result.terms[args.types] = response.entries;
          result.total_pages = response.total_pages;
          result.page = response.page;
          result.total_entries = response.total_entries;

          return result;
        } else {
          return null;
        }
      } else if (Array.isArray(args?.types)) {
        const promises: Promise<{ entries: SonicTermData[] | null } | null>[] =
          [];
        args?.types.forEach((type) => {
          promises.push(getTerms(type, undefined, -1));
        });

        const responses = await Promise.all(promises);

        if (responses) {
          responses.forEach((response, i) => {
            if (args?.types?.[i] && response) {
              result.terms[args.types[i]] = response.entries ?? [];
            }
          });
          return result;
        } else {
          return null;
        }
      } else {
        const [
          games,
          player_types,
          teams,
          schools,
          project_type,
          event_type,
          topic_type,
          courses,
        ] = await Promise.all([
          getTerms("games", undefined, -1),
          getTerms("player_types", undefined, -1),
          getTerms("teams", undefined, -1),
          getTerms("schools", undefined, -1),
          getTerms("project_type", undefined, -1),
          getTerms("event_type", undefined, -1),
          getTerms("topic_type", undefined, -1),
          getTerms("courses", undefined, -1),
        ]);

        result.terms["games"] = games?.entries ? games.entries : [];
        result.terms["player_types"] = player_types?.entries
          ? player_types.entries
          : [];
        result.terms["teams"] = teams?.entries ? teams.entries : [];
        result.terms["schools"] = schools?.entries ? schools.entries : [];
        result.terms["project_type"] = project_type?.entries
          ? project_type.entries
          : [];
        result.terms["event_type"] = event_type?.entries
          ? event_type.entries
          : [];
        result.terms["topic_type"] = topic_type?.entries
          ? topic_type.entries
          : [];
        result.terms["courses"] = courses?.entries ? courses.entries : [];

        return result;
      }
    } catch (error) {
      return null;
    }
  }
);

export const updateTerm = createAsyncThunk(
  "taxonomy/updateTerm",
  async (args: {
    auth_token: string;
    name?: string;
    id?: string;
    tax: string;
    color?: string;
    img?: File;
  }) => {
    try {
      const response = await postUpdateTerm(
        args.auth_token,
        args.name,
        args.id,
        args.tax,
        args.color,
        args.img
      );
      if (response == true) {
        return true;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }
);

export const getPickupTerms = createAsyncThunk(
  "taxonomy/getPickupTerms",
  async (args: { type: string }) => {
    const res = await getFeaturedTerms(args.type);

    if (res === null) return null;

    const terms: Array<SonicTermData> = [];
    res.terms.forEach((term) => terms.push({ ...term }));

    return {
      type: args.type,
      terms: terms,
    };
  }
);

export const setFeaturedTerms = createAsyncThunk(
  "taxonomy/setFeaturedTerms",
  async (args: { auth_token: string; ids: Array<string>; type: string }) => {
    const res = await postUpdateFeaturedTerms(
      args.auth_token,
      args.ids,
      args.type
    );

    if (res == true) {
      return true;
    } else {
      return null;
    }
  }
);

export const searchTerms = createAsyncThunk(
  "taxonomy/searchTerms",
  async (args: {
    type: string;
    page?: number;
    per?: number;
    search: string;
    desc?: boolean;
  }) => {
    const response = await getTerms(
      args.type,
      args.page,
      args.per,
      args.search,
      args.desc
    );

    if (response === null) return null;

    return response;
  }
);

export const taxSlice = createSlice({
  name: "taxonomy",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(requestTaxonomies.pending, (state) => {
        state.taxonomies = [];
      })
      .addCase(requestTaxonomies.rejected, (state) => {
        state.taxonomies = [];
      })
      .addCase(requestTaxonomies.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.taxonomies = action.payload;
        }
      })
      .addCase(requestTerms.pending, (state) => {
        state.total_pages = 0;
        state.total_entries = 0;
        state.page = 1;
        state.terms = {};
      })
      .addCase(requestTerms.rejected, () => {})
      .addCase(requestTerms.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.total_pages = action.payload.total_pages;
          state.total_entries = action.payload.total_entries;
          state.page = action.payload.page;
          state.terms = action.payload.terms;
        }
      })
      .addCase(getPickupTerms.pending, (state) => {
        state.pickups = {};
      })
      .addCase(getPickupTerms.rejected, () => {})
      .addCase(getPickupTerms.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.pickups[action.payload.type] = action.payload.terms;
        }
      })
      .addCase(searchTerms.pending, (state, action) => {
        state.terms[action.meta.arg.type] = [];
      })
      .addCase(searchTerms.rejected, () => {})
      .addCase(searchTerms.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.terms[action.meta.arg.type] = action.payload.entries;
        }
      });
  },
});

export default taxSlice.reducer;

export const getTaxonomiesData = (state: RootState) =>
  state.taxonomy.taxonomies;
export const getTermsTotalPages = (state: RootState) =>
  state.taxonomy.total_pages;
export const getTermsPage = (state: RootState) => state.taxonomy.page;
export const getTermsTotalEntries = (state: RootState) =>
  state.taxonomy.total_entries;
export const getTermsData = (state: RootState) => state.taxonomy.terms;
export const getPickupTermData = (state: RootState) => state.taxonomy.pickups;
